import {ThumbsdownIcon, ThumbsupIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {useRef, useState} from 'react'
import styles from './Buttons.module.css'
import {NegativeFeedbackForm} from './NegativeFeedbackForm'
import type {FeedbackSubmitHandler, FeedbackOptions} from './types'

/**
 * After a vote in the reason-menu flow, both thumbs stay visible but ignore clicks until vote switching ships.
 * They stay focusable (aria-disabled instead of disabled) and the chosen one is shown as pressed.
 */
interface VotedStateProps {
  ariaDisabled?: boolean
  selected?: boolean
}

const votedStateProps = ({ariaDisabled = false, selected = false}: VotedStateProps) =>
  ariaDisabled
    ? {
        'aria-disabled': true,
        'aria-pressed': selected,
        className: selected ? `${styles.Voted} ${styles.Selected}` : styles.Voted,
      }
    : {}

interface PositiveFeedbackButtonProps extends VotedStateProps {
  disabled: boolean
  commentId: string
  onClick: FeedbackSubmitHandler
}

export const PositiveFeedbackButton: React.FC<PositiveFeedbackButtonProps> = ({
  onClick,
  commentId,
  disabled,
  ariaDisabled = false,
  selected = false,
}) => (
  <IconButton
    size="small"
    variant="invisible"
    disabled={disabled}
    {...votedStateProps({ariaDisabled, selected})}
    icon={ThumbsupIcon}
    title="Positive feedback"
    aria-label="Positive feedback"
    onClick={() => {
      if (!ariaDisabled) onClick({type: 'POSITIVE', commentId})
    }}
  />
)

interface NegativeFeedbackButtonProps extends VotedStateProps {
  disabled: boolean
  commentId: string
  onClick: FeedbackSubmitHandler
  feedbackOptions?: FeedbackOptions | null
  showNegativeFeedbackModal?: boolean
}

export const NegativeFeedbackButton: React.FC<NegativeFeedbackButtonProps> = ({
  onClick,
  commentId,
  disabled,
  feedbackOptions,
  showNegativeFeedbackModal = true,
  ariaDisabled = false,
  selected = false,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const returnFocusRef = useRef<HTMLButtonElement>(null)

  const handleSubmit: FeedbackSubmitHandler = args => {
    onClick(args)
    setDialogOpen(false)
  }

  const handleClick = () => {
    if (ariaDisabled) return
    if (showNegativeFeedbackModal) {
      setDialogOpen(true)
    } else {
      // Submit negative feedback directly without showing modal
      onClick({type: 'NEGATIVE', commentId})
    }
  }

  return (
    <>
      {dialogOpen && showNegativeFeedbackModal && (
        <NegativeFeedbackForm
          onClose={() => setDialogOpen(false)}
          onSubmit={handleSubmit}
          commentId={commentId}
          feedbackOptions={feedbackOptions}
          returnFocusRef={returnFocusRef}
        />
      )}
      <IconButton
        ref={returnFocusRef}
        size="small"
        variant="invisible"
        disabled={disabled}
        {...votedStateProps({ariaDisabled, selected})}
        icon={ThumbsdownIcon}
        title="Negative feedback"
        aria-label="Negative feedback"
        onClick={handleClick}
      />
    </>
  )
}
